import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { CreateCostOfGoods, UpdateCostOfGoods } from '@repo/backend/crt'
import {
  COST_OF_GOODS_CATEGORIES,
  COST_OF_GOODS_CREATE_ERROR_TYPES,
  COST_OF_GOODS_YEAR_SUFFIXES,
  PRODUCT_CATEGORIES,
} from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { monthIndex } from '@repo/zod-schemas'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const costOfGoodsSearchSchema = z.object({
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export const costOfGoodsDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  category: z.enum(COST_OF_GOODS_CATEGORIES).optional(),
  year: z.enum(COST_OF_GOODS_YEAR_SUFFIXES).optional(),
  month: monthIndex.optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  ...datatableSearchSchema.shape,
})

export type CostOfGoodsSearch = z.infer<typeof costOfGoodsSearchSchema>
export type CostOfGoodsDetailsSearch = z.infer<typeof costOfGoodsDetailsSearchSchema>

export type CostOfGoodsList = Awaited<ReturnType<typeof getCostOfGoodsList>>
export type CostOfGoodsItem = CostOfGoodsList['results'][number]
export type CostOfGoodsWithDetails = Awaited<ReturnType<typeof getCostOfGoodsById>>

type UseCreateCostOfGoodsArgs = {
  queries: CostOfGoodsSearch
  handleOnSuccess: (costOfGoodsId: number) => void
}

type UseUpdateCostOfGoodsArgs = QueryHandlers & {
  costOfGoodsId: number
}

type UseDeleteCostOfGoodsArgs = QueryHandlers & {
  costOfGoodsIds: number[]
  queries: CostOfGoodsSearch
}

async function getCostOfGoodsList(search: CostOfGoodsSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['cost-of-goods'].$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function getCostOfGoodsById(
  id: string,
  queries: CostOfGoodsDetailsSearch = {},
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['cost-of-goods'][':id'].$get(
    {
      param: { id },
      query: stringifyValues(queries),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function getCurrentCostOfGoods(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['cost-of-goods'].current.$get(
    { query: stringifyValues({ crtPeriod }) },
    { init: { signal } },
  )
  return await res.json()
}

export const currentCostOfGoodsQueryOptions = (crtPeriod?: string) => ({
  queryKey: ['currentCostOfGoods', crtPeriod ?? null] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getCurrentCostOfGoods(crtPeriod, signal),
})

export function useCurrentCostOfGoods(crtPeriod?: string) {
  return useQuery(currentCostOfGoodsQueryOptions(crtPeriod))
}

async function createCostOfGoods(costOfGoodsDetails: CreateCostOfGoods) {
  const api = getApi()

  const res = await api.crt['cost-of-goods'].$post({
    json: costOfGoodsDetails,
  })

  return await res.json()
}

async function updateCostOfGoods(costOfGoodsId: number, details: UpdateCostOfGoods) {
  const api = getApi()

  await api.crt['cost-of-goods'][':id'].$patch({
    param: { id: String(costOfGoodsId) },
    json: details,
  })
}

async function deleteCostOfGoods(costOfGoodsIds: number[]) {
  const api = getApi()

  await api.crt['cost-of-goods'].bulk.$delete({
    json: { ids: costOfGoodsIds },
  })
}

async function getCostOfGoodsDetailsRawCsv(id: string, signal?: AbortSignal) {
  return fetchCsvExport(`/crt/cost-of-goods/${id}/details/raw-export`, signal)
}

async function getCostOfGoodsDetailsFilteredCsv(
  id: string,
  queries: CostOfGoodsDetailsSearch,
  signal?: AbortSignal,
) {
  const queryParams = new URLSearchParams(stringifyValues(queries)).toString()
  return fetchCsvExport(`/crt/cost-of-goods/${id}/details/export?${queryParams}`, signal)
}

export const costOfGoodsDetailsRawExportQueryOptions = (id: string) => ({
  queryKey: ['costOfGoodsDetailsRawExport', id],
  queryFn: ({ signal }: { signal: AbortSignal }) => getCostOfGoodsDetailsRawCsv(id, signal),
})

export const costOfGoodsDetailsExportQueryOptions = (
  id: string,
  queries: CostOfGoodsDetailsSearch = {},
) => ({
  queryKey: ['costOfGoodsDetailsExport', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getCostOfGoodsDetailsFilteredCsv(id, queries, signal),
})

export function useCostOfGoods(queries: CostOfGoodsSearch) {
  return useQuery({
    queryKey: ['costOfGoods', queries],
    queryFn: ({ signal }) => getCostOfGoodsList(queries, signal),
  })
}

export const costOfGoodsByIdQueryOptions = (
  id: string,
  queries: CostOfGoodsDetailsSearch = {},
) => ({
  queryKey: ['costOfGoodsById', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getCostOfGoodsById(id, queries, signal),
})

export function useCostOfGoodsById(id: string, queries: CostOfGoodsDetailsSearch) {
  return useQuery(costOfGoodsByIdQueryOptions(id, queries))
}

export function useCreateCostOfGoods({ queries, handleOnSuccess }: UseCreateCostOfGoodsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['costOfGoods', queries],
    mutationFn: createCostOfGoods,
    onSuccess: (costOfGoods) => {
      handleOnSuccess(costOfGoods.id)

      queryClient.invalidateQueries({
        queryKey: ['costOfGoods', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('costOfGoods.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const customParams = issues?.find((i) => i.code === 'custom')?.params as
        | { type?: string; crtPeriod?: string }
        | undefined

      let description: string
      switch (customParams?.type) {
        case COST_OF_GOODS_CREATE_ERROR_TYPES.crtPeriodConflict:
          description = `${t('errors.costOfGoods.createFailed', { ns: 'crt' })}: ${t(
            'errors.costOfGoods.crtPeriodConflict',
            { ns: 'crt', crtPeriod: customParams.crtPeriod },
          )}`
          break
        default:
          description = getFirstZodErrorMessage(issues, t, 'errors.costOfGoods.createFailed', 'crt')
      }

      toast.error(t('toast.error', { ns: 'common' }), {
        description,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateCostOfGoods({ costOfGoodsId, handleOnSuccess }: UseUpdateCostOfGoodsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateCostOfGoods', costOfGoodsId],
    mutationFn: (details: UpdateCostOfGoods) => updateCostOfGoods(costOfGoodsId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['costOfGoodsById', String(costOfGoodsId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('costOfGoods.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(issues, t, 'errors.costOfGoods.updateFailed', 'crt')

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteCostOfGoods({
  costOfGoodsIds,
  queries,
  handleOnSuccess,
}: UseDeleteCostOfGoodsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteCostOfGoods'],
    mutationFn: () => deleteCostOfGoods(costOfGoodsIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['costOfGoods', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('costOfGoods.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(issues, t, 'errors.costOfGoods.deleteFailed', 'crt')

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
